import { encode } from 'iconv-lite'
import { useState, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Input, Label } from 'reactstrap'

import { importShift, selectPlansStatus } from 'slices/plansSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES, exportData } from 'slices/utils'

import { CustomModal, FileInput } from 'components/common'
import type { CharacterCodeType } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'
import { CHARACTER_CODE_TYPES, CharacterCodeSelect } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'

import type { LoadFilePropsWithoutEncoding } from 'hooks/useImportCsv'
import { useImportCsv } from 'hooks/useImportCsv'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
}

const SHIFT_IMPORT_CSV_HEADER = ['日付', '名前', '識別子', '予定テンプレート', 'シフト開始時間', 'シフト終了時間']
const MAX_SHIFT_IMPORT_COLUMNS = (50000).toLocaleString()

const ShiftImport = ({ isOpen, onSuccess, onCancel }: Props) => {
  const { importDisabled, setFile, importCsvFileWithOutEncoding, modalErrorMessage, setModalErrorMessage } =
    useImportCsv()
  const [submitted, setSubmitted] = useState(false)
  const [characterCode, setCharacterCode] = useState<CharacterCodeType>(CHARACTER_CODE_TYPES.SHIFT_JIS)
  const [applyScheduleTemplate, setApplyScheduleTemplate] = useState(false)
  const dispatch = useDispatch()
  const { isRequesting, errorMessage } = useSelector(selectPlansStatus, shallowEqual)

  const handleSaveClick = () => {
    const handleFileLoad = (data: LoadFilePropsWithoutEncoding) => {
      const requestBody = {
        fileName: data.fileName,
        encoding: characterCode,
        applyTemplate: applyScheduleTemplate,
      }
      dispatch(importShift(requestBody, data.content))
      setSubmitted(true)
    }
    importCsvFileWithOutEncoding(handleFileLoad)
  }

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      setModalErrorMessage(undefined)
      onSuccess()
    } else {
      if (ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        // ENABLE_DIALOGのときにはエラーダイアログが出るのでNotificationは出さない
        setModalErrorMessage(undefined)
      } else {
        setModalErrorMessage('保存できませんでした。')
      }
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess, setModalErrorMessage])

  const handleCancelClick = () => {
    setModalErrorMessage(undefined)
    setSubmitted(false)
    setApplyScheduleTemplate(false)
    onCancel()
  }

  const handleCharacterCodeChange = (value: CharacterCodeType) => {
    setCharacterCode(value)
  }

  const handleDownloadFormat = () => {
    const formatHeader = [SHIFT_IMPORT_CSV_HEADER].map(row => row.join(',')).join('\n')
    const csvData = encode(formatHeader, characterCode)
    const blob = new Blob([csvData], { type: 'text/csv' })
    exportData(blob, 'シフト登録シート.csv')
  }

  return (
    <CustomModal
      isOpen={isOpen}
      title="シフトインポート"
      approveDisabled={importDisabled}
      errorMessage={modalErrorMessage}
      onCancel={handleCancelClick}
      onApprove={handleSaveClick}
      onHideNotification={() => setModalErrorMessage(undefined)}
      submitName="shift-import-submit"
    >
      <div className="pb-4">
        <div className="my-3">
          下記シフト登録用シートをダウンロードして、同じフォーマットでCSVアップロードをしてください。
        </div>
        <div className="w-60 mb-3">
          <CharacterCodeSelect characterCode={characterCode} onChange={handleCharacterCodeChange} />
        </div>
        <ul className="mb-3">
          <li>
            シフトインポートについて詳しくは
            <a
              className="text-decoration-none"
              target="_blank"
              rel="noreferrer"
              href="https://help.smileboard.jp/shift-regularmember"
            >
              こちら
            </a>
            をご確認ください。
          </li>
          <li>
            {`一度にインポートできるのはヘッダーを除いて${MAX_SHIFT_IMPORT_COLUMNS}行までです。適宜分割してインポートしてください。`}
            <span className="text-danger">
              {`アップロードされたCSVファイルがヘッダーを除いて${MAX_SHIFT_IMPORT_COLUMNS}行より多い場合は処理は実施されません。`}
            </span>
          </li>
        </ul>
        <Button color="link" className="mb-3 ps-0 shadow-none text-decoration-none" onClick={handleDownloadFormat}>
          シフト登録用シートをダウンロード
        </Button>
        <div className="mb-4">
          <FileInput id="shiftFile" accept=".csv" onChange={f => setFile(f)} />
        </div>

        <div className="mb-4">
          <div className="my-2 fw-bold">その他の設定</div>
          <div className="form-check">
            <Input
              id="apply-schedule-template"
              className="form-check-input"
              checked={applyScheduleTemplate}
              type="checkbox"
              onChange={e => setApplyScheduleTemplate(e.target.checked)}
            />
            <Label className="form-check-label" for="apply-schedule-template">
              作業予定テンプレートを反映
            </Label>
          </div>
          <div className="text-danger">
            インポートするファイルに作業予定テンプレートが設定されている場合は設定に則り作業予定が自動で入力されます。すでに作業予定がある場合には上書きされます。
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default ShiftImport
